import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";

// Threshold: when active boxes exceed this, skip-frame updates kick in
const SKIP_FRAME_THRESHOLD = 5;

// The box's clip: Midnight Alchemy's export names it `win`, Zeus's `Win`.
const WIN_CLIPS = ["win", "Win"];
const winClip = (spine) =>
  WIN_CLIPS.find((name) => spine?.skeleton?.data?.findAnimation(name)) ?? WIN_CLIPS[0];

/**
 * WinBoxController - Manages per-cell win box spine animations
 *
 * Architecture: Pre-warmed spine pool with manual ticker updates
 * - One spine instance per cell (REEL_COUNT × SYMBOLS_VISIBLE = 20 cells)
 * - Each spine plays the "win" animation from winbox.json
 * - autoUpdate disabled on all spines — manual update via shared ticker
 * - When 6+ boxes active: updates every 2nd frame to halve bone processing cost
 * - Active-only tracking: show/hide/update only touches relevant spines
 */
class WinBoxController {
  constructor(container, symbolWidth, symbolHeight, reelSpacing) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;

    // One spine instance per cell, keyed by "reelIndex_symbolIndex"
    this.winBoxSpines = {};
    this.spineContainer = new PIXI.Container();
    this.initialized = false;

    // Track currently active cell keys (avoids iterating all spines)
    this.activeCellKeys = [];

    // Skip-frame update state
    this._frameCounter = 0;
    this._tickerBound = this._onTick.bind(this);
    this._tickerActive = false;

    // Internal caches
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;

    // Add spine container to main container
    this.container.addChild(this.spineContainer);

    // Initialize all spines
    this.initializeSpines();
  }

  /**
   * Initialize spine instances — one per grid cell, all pre-warmed
   */
  initializeSpines() {
    if (!this.symbolWidth || !this.symbolHeight) {
      return;
    }

    // Create a probe spine to verify the asset is available
    const probeSpine = this.createWinBoxSpine();
    if (!probeSpine || !probeSpine.skeleton || !probeSpine.state) {
      return;
    }

    // Use probe as first cell's instance
    this.warmAndStoreSpine(probeSpine, 0, 0);

    // Create remaining spines for all other cells
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        // Skip the first cell (already used probe spine)
        if (reelIndex === 0 && symbolIndex === 0) continue;

        const spine = this.createWinBoxSpine();
        if (!spine || !spine.state) continue;

        this.warmAndStoreSpine(spine, reelIndex, symbolIndex);
      }
    }

    // Start with container hidden (GPU still processes at alpha 0.001)
    this.spineContainer.alpha = 0.001;

    // Schedule GPU warmup cleanup — wait 3 frames for renderer to compile
    // shaders and upload vertex/texture data, then pause all spines cleanly
    this.scheduleWarmupCleanup();

    this.initialized = true;
  }

  /**
   * Wait 3 GPU frames after warmup to ensure PixiJS renderer has drawn
   * all spine meshes and compiled shaders/uploaded buffers. Then pause cleanly.
   * (Same pattern as OverlayReelController.scheduleWarmupCleanup)
   */
  scheduleWarmupCleanup() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const cellKeys = Object.keys(this.winBoxSpines);
          for (let i = 0; i < cellKeys.length; i++) {
            const spine = this.winBoxSpines[cellKeys[i]];
            if (spine?.state) {
              spine.state.clearTracks();
              spine.state.timeScale = 0;
              spine.autoUpdate = false;
              spine.visible = false; // Skip render tree entirely (GPU cost = 0)
            }
          }
          this.spineContainer.visible = false;
        });
      });
    });
  }

  /**
   * Warm up a spine instance and store it in the pool
   * @param {Spine} spine - The spine instance to warm
   * @param {number} reelIndex - Reel index (0-4)
   * @param {number} symbolIndex - Symbol row index (0-3)
   */
  warmAndStoreSpine(spine, reelIndex, symbolIndex) {
    // autoUpdate OFF — we control updates manually via ticker
    spine.autoUpdate = false;
    this.spineContainer.addChild(spine);

    // GPU invisible but still in render pipeline
    spine.alpha = 0.001;

    // Position at the cell coordinates
    this.positionSpine(spine, reelIndex, symbolIndex);

    // Scale to fit symbol cell
    this.scaleSpine(spine);

    // Set win animation → triggers shader compilation and mesh creation
    // spine.state.setAnimation(0, "Win", true);
    spine.state.setAnimation(0, winClip(spine), true);
    spine.state.update(0.016);

    // Populate bone/slot hierarchy for GPU
    try {
      spine.skeleton.updateWorldTransform();
    } catch (e) {
      // Physics not initialized on some spines — safe to continue
    }

    // Don't pause yet — leave spine rendering at alpha 0.001 so GPU
    // compiles shaders during the 3-frame warmup window.
    // scheduleWarmupCleanup() will clear tracks and pause after 3 frames.

    const cellKey = `${reelIndex}_${symbolIndex}`;
    this.winBoxSpines[cellKey] = spine;
  }

  /**
   * Position a spine at a specific cell
   */
  positionSpine(spine, reelIndex, symbolIndex) {
    // Center of reel: reelIndex offset from center, using reelSpacing
    const x = this.reelSpacing * (reelIndex - Math.floor(Settings.REEL_COUNT / 2));
    // Y position: row offset from top of reel set plus half symbol for centering
    const y =
      this.symbolHeight * symbolIndex -
      (this.symbolHeight * (Settings.SYMBOLS_VISIBLE - 1)) / 2;

    spine.position.set(x, y);
  }

  /**
   * Scale spine to fill symbol cell dimensions.
   * Uses the same approach as OverlayReelController.calculateSpineScale:
   * scale = containerHeight / Settings.SYMBOL_HEIGHT (native design size)
   */
  scaleSpine(spine) {
    const heightScale = this.symbolHeight / Settings.SYMBOL_HEIGHT;
    // Authored in the symbol rigs' units (185-unit cell), so it takes their scale.
    spine.scale.set(heightScale * (Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT ?? 1));
  }

  /**
   * Create a single win box spine instance
   */
  createWinBoxSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "winbox-skeleton";
      const atlasAlias = "winbox-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Shared ticker callback — updates only active spines manually
   * When 1-5 boxes: updates every frame (smooth)
   * When 6+ boxes: updates every 2nd frame (halves bone cost)
   */
  _onTick() {
    const activeCount = this.activeCellKeys.length;
    if (activeCount === 0) return;

    this._frameCounter++;

    // Skip every other frame when many boxes are active
    if (activeCount > SKIP_FRAME_THRESHOLD && this._frameCounter % 2 !== 0) {
      return;
    }

    // Compute delta time (in seconds)
    const dt = PIXI.Ticker.shared.deltaMS / 1000;
    // When skipping frames, compensate so animation speed stays correct
    const compensatedDt = activeCount > SKIP_FRAME_THRESHOLD ? dt * 2 : dt;

    for (let i = 0; i < activeCount; i++) {
      const cellKey = this.activeCellKeys[i];
      const spine = this.winBoxSpines[cellKey];
      if (spine && spine.state && spine.state.timeScale > 0) {
        spine.update(compensatedDt);
      }
    }
  }

  /** Start the manual update ticker */
  _startTicker() {
    if (!this._tickerActive) {
      this._frameCounter = 0;
      PIXI.Ticker.shared.add(this._tickerBound);
      this._tickerActive = true;
    }
  }

  /** Stop the manual update ticker */
  _stopTicker() {
    if (this._tickerActive) {
      PIXI.Ticker.shared.remove(this._tickerBound);
      this._tickerActive = false;
      this._frameCounter = 0;
    }
  }

  /**
   * Show win boxes at specified positions
   * @param {Array<[number, number]>} positions - Array of [reelIndex, symbolIndex] pairs
   * @param {number} [targetLoops=0] - Number of loops to play (0 = infinite loop)
   */
  // `windowMs` (optional): how long the win is up. The box then plays its
  // `targetLoops` loops in EXACTLY that time — its speed is scaled by the few
  // percent that whole loops of its clip miss by — so it ends with the
  // symbols' win, never frozen before it and never cut mid-loop (Vladimir,
  // 2026-09-30: "lines and boxes are tied together, same duration").
  showWinBoxes(positions, targetLoops = 0, windowMs = 0) {
    if (!this.initialized) return;
    if (!Array.isArray(positions) || positions.length === 0) return;

    // Hide container during setup
    this.spineContainer.visible = false;

    // Stop ticker while setting up
    this._stopTicker();

    // Pause previously active spines
    for (let i = 0; i < this.activeCellKeys.length; i++) {
      const prevKey = this.activeCellKeys[i];
      const spine = this.winBoxSpines[prevKey];
      if (spine && spine.state) {
        spine.visible = false;
        spine.state.timeScale = 0;
        spine.state.clearListeners();
      }
    }

    // Cancel any pending deferred activation from a previous call
    if (this._deferredRAF) {
      cancelAnimationFrame(this._deferredRAF);
      this._deferredRAF = null;
    }

    // Defer spine activation to next frame — splits the GPU/CPU cost
    // so win animations + dim fade get their own frame first
    this._pendingPositions = positions;
    this._pendingLoops = targetLoops;
    this._pendingWindowMs = windowMs;
    this._deferredRAF = requestAnimationFrame(() => {
      this._deferredRAF = null;
      this._activateSpines(
        this._pendingPositions,
        this._pendingLoops,
        this._pendingWindowMs,
      );
    });
  }

  /**
   * Internal: activate spine animations and show container.
   * Called from requestAnimationFrame to spread GPU load.
   */
  _activateSpines(positions, targetLoops, windowMs = 0) {
    if (!this.initialized) return;

    // Determine loop mode: single loop = play once and hold last frame
    const shouldLoop = targetLoops === 0 || targetLoops > 1;

    // Activate the requested cell spines
    const validKeys = [];
    for (let i = 0; i < positions.length; i++) {
      const [reelIndex, symbolIndex] = positions[i];
      const cellKey = `${reelIndex}_${symbolIndex}`;
      const spine = this.winBoxSpines[cellKey];
      if (!spine || !spine.state) continue;

      // setAnimation clears the current track internally — no need for clearTracks
      spine.state.clearListeners();
      // const entry = spine.state.setAnimation(0, "Win", shouldLoop);
      const entry = spine.state.setAnimation(0, winClip(spine), shouldLoop);
      spine.state.update(0);

      // For finite loops > 1, count completions and stop looping at target
      if (targetLoops > 1 && entry) {
        let completedLoops = 0;
        entry.listener = {
          complete: () => {
            completedLoops++;
            if (completedLoops >= targetLoops) {
              entry.loop = false; // Hold on last frame
            }
          },
        };
      }

      // Apply the animation BEFORE making it visible, or the first frame draws
      // the setup pose instead of the clip's first key.
      spine.state.apply(spine.skeleton);
      try {
        spine.skeleton.updateWorldTransform();
      } catch (e) {
        // Physics constraint not initialized — safe to ignore
      }

      spine.visible = true;
      spine.alpha = 1;
      // spine.state.timeScale = 1;
      spine.state.timeScale = this._fitTimeScale(spine, targetLoops, windowMs);
      validKeys.push(cellKey);
    }

    this.activeCellKeys = validKeys;

    // Start manual ticker for active spine updates
    this._startTicker();

    // Show container — all requested boxes appear at once
    this.spineContainer.visible = true;
    this.spineContainer.alpha = 1;
  }

  /**
   * Show win boxes for multiple lines simultaneously (for "show all wins" phase)
   * @param {Array<Array<[number, number]>>} allLinePositions - Array of position arrays per line
   * @param {number} [targetLoops=0] - Number of loops to play (0 = infinite loop)
   */
  showAllWinBoxes(allLinePositions, targetLoops = 0, windowMs = 0) {
    if (!this.initialized) return;
    if (!Array.isArray(allLinePositions) || allLinePositions.length === 0)
      return;

    // Deduplicate positions across all lines
    const positionSet = new Set();
    const uniquePositions = [];

    for (let i = 0; i < allLinePositions.length; i++) {
      const linePositions = allLinePositions[i];
      if (!Array.isArray(linePositions)) continue;

      for (let j = 0; j < linePositions.length; j++) {
        const [reelIndex, symbolIndex] = linePositions[j];
        const key = `${reelIndex}_${symbolIndex}`;
        if (!positionSet.has(key)) {
          positionSet.add(key);
          uniquePositions.push([reelIndex, symbolIndex]);
        }
      }
    }

    this.showWinBoxes(uniquePositions, targetLoops, windowMs);
  }

  /**
   * The speed that makes `loops` loops of the box's clip last `windowMs`.
   * 1 when either is unknown (loop forever / no window).
   */
  _fitTimeScale(spine, loops, windowMs) {
    if (!(loops > 0) || !(windowMs > 0)) return 1;
    const clipMs = (spine.state.getCurrent(0)?.animation?.duration ?? 0) * 1000;
    if (!(clipMs > 0)) return 1;
    return (loops * clipMs) / windowMs;
  }

  /**
   * Hide all active win boxes
   */
  hideWinBoxes() {
    // Cancel any pending deferred activation
    if (this._deferredRAF) {
      cancelAnimationFrame(this._deferredRAF);
      this._deferredRAF = null;
    }

    // Stop ticker
    this._stopTicker();

    // Hide container — visible=false skips entire render tree
    this.spineContainer.visible = false;

    // Pause active spines (skip setToSetupPose — _activateSpines does apply+updateWorldTransform)
    for (let i = 0; i < this.activeCellKeys.length; i++) {
      const cellKey = this.activeCellKeys[i];
      const spine = this.winBoxSpines[cellKey];
      if (spine && spine.state) {
        spine.state.timeScale = 0;
        spine.state.clearListeners();
        spine.state.clearTracks();
        spine.visible = false;
      }
    }

    this.activeCellKeys = [];
  }

  /**
   * Update spine dimensions and rescale for resize events
   */
  updateDimensionsAndRescale(symbolWidth, symbolHeight, reelSpacing) {
    if (
      this.symbolWidth === symbolWidth &&
      this.symbolHeight === symbolHeight &&
      this.reelSpacing === reelSpacing
    ) {
      return;
    }

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;

    if (!this.initialized) return;

    // Reposition and rescale all spines
    const cellKeys = Object.keys(this.winBoxSpines);
    for (let i = 0; i < cellKeys.length; i++) {
      const cellKey = cellKeys[i];
      const spine = this.winBoxSpines[cellKey];
      if (!spine) continue;

      const [reelIndex, symbolIndex] = cellKey.split("_").map(Number);
      this.positionSpine(spine, reelIndex, symbolIndex);
      this.scaleSpine(spine);
    }
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return this.initialized && Object.keys(this.winBoxSpines).length > 0;
  }

  /**
   * Clean up all spine instances and resources
   */
  cleanup() {
    if (this._deferredRAF) {
      cancelAnimationFrame(this._deferredRAF);
      this._deferredRAF = null;
    }
    this._stopTicker();

    const cellKeys = Object.keys(this.winBoxSpines);
    for (let i = 0; i < cellKeys.length; i++) {
      const spine = this.winBoxSpines[cellKeys[i]];
      // `destroyed` guard: this cleanup can run after the parent Container has
      // already taken the subtree down — see the note in WinTextController.
      if (spine && !spine.destroyed) {
        if (spine.state) {
          spine.state.clearTracks();
          spine.state.clearListeners();
        }
        if (spine.parent) {
          spine.parent.removeChild(spine);
        }
        spine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }
    this.winBoxSpines = {};

    if (this.spineContainer) {
      if (!this.spineContainer.destroyed) {
        if (this.spineContainer.parent) {
          this.spineContainer.parent.removeChild(this.spineContainer);
        }
        this.spineContainer.destroy({ children: true });
      }
      this.spineContainer = null;
    }

    this.activeCellKeys = [];
    this.initialized = false;
  }
}

export default WinBoxController;
